// TUT-450 — o omnichannel: ativação, canais, diretório e vínculos, caixa de entrada, widgets, conversas do processo e o Telegram.
import { demoApi } from "../demo-data";
import type {
  OmnichannelStatus,
  OmnichannelChannelsResponse,
  NativeOmnichannelChannel,
  OmnichannelChannelSetup,
  OmnichannelDirectoryOptions,
  OmnichannelContactLink,
  ProcessConversationsResponse,
  TutelaeInboxMessage,
  TutelaeInboxMessagesResponse,
  TutelaeInboxMetrics,
  TutelaeInboxResponse,
  TutelaeWebsiteWidget,
  TutelaeWebsiteWidgetsResponse,
  TelegramChannelSettings,
  UpdateTelegramChannelInput,
} from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDoOmnichannel = {
  omnichannelStatus(): Promise<OmnichannelStatus> {
    if (useDemoApi) return demoApi.omnichannelStatus();
    return request<OmnichannelStatus>("/omnichannel/status");
  },
  activateOmnichannel(): Promise<OmnichannelStatus> {
    if (useDemoApi) return demoApi.activateOmnichannel();
    return request<OmnichannelStatus>("/omnichannel/activate", {
      method: "POST",
    });
  },
  omnichannelSso(): Promise<{ url: string }> {
    if (useDemoApi) return demoApi.omnichannelSso();
    return request<{ url: string }>("/omnichannel/sso", { method: "POST" });
  },
  omnichannelChannels(): Promise<OmnichannelChannelsResponse> {
    if (useDemoApi) return demoApi.omnichannelChannels();
    return request<OmnichannelChannelsResponse>("/omnichannel/channels");
  },
  prepareOmnichannelChannel(
    key: NativeOmnichannelChannel,
    inboxId?: number,
  ): Promise<OmnichannelChannelSetup> {
    if (useDemoApi) return demoApi.prepareOmnichannelChannel(key, inboxId);
    return request<OmnichannelChannelSetup>("/omnichannel/channels/setup", {
      method: "POST",
      body: JSON.stringify({ key, ...(inboxId ? { inboxId } : {}) }),
    });
  },
  verifyOmnichannelChannel(inboxId: number): Promise<{
    verified: true;
    verifiedAt: string;
  }> {
    if (useDemoApi) return demoApi.verifyOmnichannelChannel(inboxId);
    return request(`/omnichannel/channels/${inboxId}/verify`, {
      method: "POST",
    });
  },
  disconnectOmnichannelChannel(
    inboxId: number,
  ): Promise<{ disconnected: true; id: number }> {
    if (useDemoApi) return demoApi.disconnectOmnichannelChannel(inboxId);
    return request(`/omnichannel/channels/${inboxId}`, { method: "DELETE" });
  },
  omnichannelDirectoryOptions(): Promise<OmnichannelDirectoryOptions> {
    if (useDemoApi) return demoApi.omnichannelDirectoryOptions();
    return request<OmnichannelDirectoryOptions>(
      "/omnichannel/contacts/directory-options",
    );
  },
  linkOmnichannelContact(
    contactId: number,
    input: { personId: string | null; organizationId: string | null },
  ): Promise<OmnichannelContactLink> {
    if (useDemoApi) return demoApi.linkOmnichannelContact(contactId, input);
    return request<OmnichannelContactLink>(
      `/omnichannel/contacts/${contactId}/directory-link`,
      { method: "PUT", body: JSON.stringify(input) },
    );
  },
  unlinkOmnichannelContact(contactId: number): Promise<{ removed: true }> {
    if (useDemoApi) return demoApi.unlinkOmnichannelContact(contactId);
    return request<{ removed: true }>(
      `/omnichannel/contacts/${contactId}/directory-link`,
      { method: "DELETE" },
    );
  },
  inboxConversations(page = 1): Promise<TutelaeInboxResponse> {
    if (useDemoApi) return demoApi.inboxConversations(page);
    return request<TutelaeInboxResponse>(
      `/omnichannel/conversations?page=${page}`,
    );
  },
  inboxMessages(conversationId: number): Promise<TutelaeInboxMessagesResponse> {
    if (useDemoApi) return demoApi.inboxMessages(conversationId);
    return request<TutelaeInboxMessagesResponse>(
      `/omnichannel/conversations/${conversationId}/messages`,
    );
  },
  sendInboxMessage(
    conversationId: number,
    content: string,
    isPrivate = false,
  ): Promise<TutelaeInboxMessage> {
    if (useDemoApi)
      return demoApi.sendInboxMessage(conversationId, content, isPrivate);
    return request<TutelaeInboxMessage>(
      `/omnichannel/conversations/${conversationId}/messages`,
      {
        method: "POST",
        body: JSON.stringify({ content, private: isPrivate }),
      },
    );
  },
  websiteWidgets(): Promise<TutelaeWebsiteWidgetsResponse> {
    if (useDemoApi) return demoApi.websiteWidgets();
    return request<TutelaeWebsiteWidgetsResponse>("/omnichannel/widgets");
  },
  createWebsiteWidget(input: {
    name: string;
    websiteUrl: string;
  }): Promise<TutelaeWebsiteWidget> {
    if (useDemoApi) return demoApi.createWebsiteWidget(input);
    return request<TutelaeWebsiteWidget>("/omnichannel/widgets", {
      method: "POST",
      body: JSON.stringify(input),
    });
  },
  inboxMetrics(from?: string, to?: string): Promise<TutelaeInboxMetrics> {
    if (useDemoApi) return demoApi.inboxMetrics(from, to);
    const params = new URLSearchParams();
    if (from) params.set("from", from);
    if (to) params.set("to", to);
    const query = params.size ? `?${params}` : "";
    return request<TutelaeInboxMetrics>(`/omnichannel/metrics${query}`);
  },
  processConversations(
    processId: string,
  ): Promise<ProcessConversationsResponse> {
    if (useDemoApi) return demoApi.processConversations(processId);
    return request<ProcessConversationsResponse>(
      `/omnichannel/processes/${encodeURIComponent(processId)}/conversations`,
    );
  },
  linkProcessConversation(
    processId: string,
    conversationId: number,
  ): Promise<ProcessConversationsResponse> {
    if (useDemoApi)
      return demoApi.linkProcessConversation(processId, conversationId);
    return request<ProcessConversationsResponse>(
      `/omnichannel/processes/${encodeURIComponent(processId)}/conversations/${conversationId}`,
      { method: "PUT" },
    );
  },
  unlinkProcessConversation(
    processId: string,
    conversationId: number,
  ): Promise<ProcessConversationsResponse> {
    if (useDemoApi)
      return demoApi.unlinkProcessConversation(processId, conversationId);
    return request<ProcessConversationsResponse>(
      `/omnichannel/processes/${encodeURIComponent(processId)}/conversations/${conversationId}`,
      { method: "DELETE" },
    );
  },
  updateTelegramChannel(
    input: UpdateTelegramChannelInput,
  ): Promise<TelegramChannelSettings> {
    if (useDemoApi) return demoApi.updateTelegramChannel(input);
    return request<TelegramChannelSettings>("/notification-channels/telegram", {
      method: "PUT",
      body: JSON.stringify(input),
    });
  },
};
